'use client';

import * as React from 'react';
import { CheckIcon, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
	Command,
	CommandGroup,
	CommandItem,
	CommandList,
} from '@/components/ui/command';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import clsx from 'clsx';
import { Icon, Spinner, Typography, TypographyType } from '../common';
import Icons from '@/const/icons';
import { InputText } from '../form';
import { useForm } from 'react-hook-form';

export type Options = {
	icon?: JSX.Element | string;
	label?: string | React.ReactNode;
	value?: string;
	options?: Options[];
	title?: string;
	onClick?: () => void;
};

interface IProps {
	open: boolean;
	value?: string;
	placeholder: string;
	options: Options[];
	classNameOptions?: string;
	isLoading?: boolean;
	title?: string;
	classNameTitle?: string;
	buttonContent?: string | JSX.Element;
	classNameTrigger?: string;
	classNameButton?: string;
	classNamePopoverContent?: string;
	classNameCommand?: string;
	classNameCommandList?: string;
	classNameCommandGroup?: string;
	classNameCommandItem?: string;
	classNameClear?: string;
	typographyTypeLabel?: TypographyType;
	isSearch?: boolean;
	align?: 'center' | 'end' | 'start' | undefined;
	defaultSelected?: Options;
	disabled?: boolean;
	containerRef?: (node: HTMLDivElement | null) => void;
	clearAll?: () => void;
	children?: React.ReactNode;
	setOpen: (value: boolean) => void;
	setValue?: (value?: string) => void;
	handleSearch?: (value: string) => void;
	hasValue?: boolean;
}

export const Combobox = ({
	placeholder,
	options,
	title,
	classNameTitle,
	open,
	setOpen,
	value,
	setValue,
	clearAll,
	typographyTypeLabel,
	buttonContent,
	containerRef,
	classNameTrigger,
	classNameButton,
	classNamePopoverContent,
	classNameCommand,
	classNameCommandItem,
	classNameCommandList,
	classNameCommandGroup,
	classNameClear,
	handleSearch,
	isSearch,
	classNameOptions,
	isLoading,
	align = 'start',
	defaultSelected,
	disabled,
	children,
	hasValue,
}: IProps): React.JSX.Element => {
	const { register } = useForm();
	const [search, setSearch] = React.useState<string>('');
	const [selected, setSelected] = React.useState<Options | undefined>(
		defaultSelected
	);

	// const label = options.find(
	// 	(option) => option.value?.toLowerCase() === value?.toLowerCase()
	// )?.label;

	const filteredOptions = options?.filter((option) =>
		typeof option.label === 'string'
			? (option.label as string)?.toLowerCase().includes(search?.toLowerCase())
			: true
	);
	const isSelected = (
		selected: Options | undefined | null,
		option: Options
	): boolean => {
		return !!selected && selected.value === option.value; // changed to check only the value because it was causing the option to not be selected when the label was a React node, if any where in the app two options look selected at the same time its because the have the same value
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				disabled={disabled}
				asChild={!buttonContent}
				className={clsx(
					`p-2 !text-b14 !text-black border-smoke-50 rounded-lg justify-between md:justify-start w-full z-[60] disabled:cursor-not-allowed`,
					classNameTrigger
				)}
			>
				{buttonContent ? (
					buttonContent
				) : (
					<Button
						variant="outline"
						aria-expanded={open}
						className={clsx(
							'relative gap-2 min-w-max bg-white',
							classNameButton
						)}
					>
						<Typography
							type="semibold-14"
							className={clsx(
								'!line-clamp-1 flex-1 text-ellipsis overflow-hidden whitespace-normal text-left opacity-60'
							)}
						>
							{/* {value ? label : placeholder} */}
							{placeholder}
						</Typography>
						<ChevronDown
							className={`mt-0.5 h-5 w-5 shrink-0 text-[#0D0616] ${
								open && 'rotate-180'
							}`}
						/>
						{(value || hasValue) && (
							<span className="w-[10px] h-[10px] bg-main-blue rounded-full absolute -top-1 -right-1 z-20" />
						)}
					</Button>
				)}
			</PopoverTrigger>
			<PopoverContent
				onOpenAutoFocus={(e) => e.preventDefault()}
				className={clsx(
					'w-full max-h-96 md:w-max p-0 border-none overflow-y-auto scroll-custom shadow-[0px_2px_5px_0px_#0000003D]',
					classNamePopoverContent
				)}
				align={align}
			>
				{children ? (
					children
				) : (
					<Command className={clsx('bg-white text-black', classNameCommand)}>
						{title && (
							<Typography
								type="semibold-16"
								className={clsx('mb-2 px-4 pt-3', classNameTitle)}
							>
								{title}
							</Typography>
						)}

						{/* THIS IS A INPUT SEARCH CAN BE IMPLEMENTED IF ITS NECESSARY */}
						{/* <CommandInput className="!text-black" placeholder="Search framework..." /> */}
						<CommandList className={classNameCommandList}>
							{/* <CommandEmpty>No items found.</CommandEmpty> */}

							<CommandGroup className={clsx('!p-0', classNameCommandGroup)}>
								{isSearch && (
									<div className="px-2">
										<InputText
											autoFocus={false}
											name="search"
											register={register}
											placeholder="Search"
											iconLeft={Icons.search}
											className="w-full my-4"
											classNameIcon={'!top-[23%] !w-4 !h-4 text-opacity-black'}
											classNameInput="pl-9 !bg-gray-15 !h-[33px] !m-0 border-none 3xl:!text-sm"
											onChange={({ target }) => {
												console.log({ target });
												if (handleSearch) {
													handleSearch(target.value);
												} else {
													setSearch(target.value);
												}
											}}
										/>
									</div>
								)}
								<div className={classNameOptions}>
									{clearAll && (
										<Button
											variant={'ghost'}
											className={clsx(
												'font-normal flex justify-start items-center gap-2 h-12 px-4 w-full hover:bg-grey-100 rounded-none',
												classNameClear
											)}
											onClick={() => {
												clearAll();
												setOpen(false);
												setSelected(undefined);
											}}
										>
											<Icon src={Icons.broom} className="!w-5 !h-5" />
											<Typography type={typographyTypeLabel || 'regular-14'}>
												Clear All
											</Typography>
										</Button>
									)}
									{isLoading ? (
										<div className="w-ful flex items-center justify-center p-6">
											<Spinner type="loadingPage" />
										</div>
									) : (
										filteredOptions?.map((option, index) => {
											const isLastElement = options.length === index + 1;
											return (
												<CommandItem
													ref={isLastElement ? containerRef : null}
													key={`list-${option.value}-${index}`}
													value={option.value}
													className={clsx(
														'w-full bg-white py-3  !px-4 !max-w-full" flex items-center gap-2 cursor-pointer hover:bg-grey-100',
														classNameCommandItem,
														{ 'bg-grey-100': isSelected(selected, option) }
													)}
													onSelect={(currentValue) => {
														setValue?.(
															currentValue === value ? '' : currentValue
														);
														setOpen(false);
														setSelected(option);
														option.onClick?.();
													}}
												>
													{option.icon && <div>{option.icon}</div>}
													<Typography
														type={typographyTypeLabel || 'regular-14'}
														className="line-clamp-1 text-ellipsis w-full"
													>
														{option.label}
													</Typography>

													{isSelected(selected, option) &&
													option.value !== 'clear-all' ? (
														<span className="absolute right-0 pr-4">
															<CheckIcon
																className="w-5 h-5 text-black"
																aria-hidden="true"
															/>
														</span>
													) : null}
												</CommandItem>
											);
										})
									)}
								</div>
							</CommandGroup>
						</CommandList>
					</Command>
				)}
			</PopoverContent>
		</Popover>
	);
};
